import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import DataLoadError from '../components/DataLoadError'
import {
  Beef,
  Map,
  DollarSign,
  Flame,
  TrendingUp,
  AlertCircle,
  AlertTriangle,
  Bell,
  CheckCircle,
  X,
  Plus,
  ArrowRight,
  RefreshCw,
  Clock,
  Activity,
  ChevronRight,
  XCircle,
  Info,
  Satellite,
  FileText,
  Camera,
} from 'lucide-react'
import {
  getDashboard,
  getAgentDashboard,
  getDiscrepancies,
  getAgentAlerts,
  dismissAlert,
  resolveDiscrepancy,
  dismissDiscrepancy,
  getGrazingRecommendations,
  getRemoteSensingSnapshot,
  getDashboardMetrics,
  getDashboardAnomalies,
  getLatestReport,
} from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import StatCard from '../components/StatCard'
import PinComplianceCard from '../components/PinComplianceCard'
import ActivityPulseCard from '../components/dashboard/ActivityPulseCard'
import HealthInterventionsCard from '../components/dashboard/HealthInterventionsCard'
import WeighingTrendCard from '../components/dashboard/WeighingTrendCard'
import PaddockHealthCard from '../components/dashboard/PaddockHealthCard'
import GrazingNowCard from '../components/dashboard/GrazingNowCard'
import GrazingTrackerCard from '../components/dashboard/GrazingTrackerCard'
import RegistrationCard from '../components/dashboard/RegistrationCard'
import KraalFeedCard from '../components/dashboard/KraalFeedCard'
import clsx from 'clsx'

// =============================================================================
// SHARED HELPERS
// =============================================================================

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-UG', {
    style: 'currency',
    currency: 'UGX',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

// =============================================================================
// ACTION REQUIRED STRIP (Owner-View Plan, Session 1)
// =============================================================================
// Sticky-at-top severity-grouped exception strip. Replaces the older
// AnomalyCards component which lived buried mid-page. Persists collapsed
// state in localStorage so an owner who has already triaged today's list
// doesn't re-expand it on every navigation.

const SEVERITY_CONFIG = {
  critical: { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-800', dot: 'bg-red-500', icon: XCircle, label: 'Critical' },
  warning:  { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-800', dot: 'bg-amber-500', icon: AlertTriangle, label: 'Warning' },
  info:     { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', dot: 'bg-blue-500', icon: Info, label: 'Info' },
}

function ActionRequiredStrip({ ranchId }) {
  const storageKey = `dashboard.actionRequired.collapsed.${ranchId}`
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(storageKey) === '1' } catch { return false }
  })

  const { data, isLoading } = useQuery({
    queryKey: ['dashboardAnomaliesGrouped', ranchId],
    queryFn: ({ signal }) => getDashboardAnomalies(ranchId, { signal, grouped: true }),
    staleTime: 60_000,
    refetchInterval: 60_000,
  })

  if (isLoading || !data) return null
  const total = data.total ?? 0
  if (total === 0) return null

  const setAndPersist = (next) => {
    setCollapsed(next)
    try { localStorage.setItem(storageKey, next ? '1' : '0') } catch { /* localStorage may be disabled (e.g. private mode) */ }
  }

  const counts = {
    critical: data.critical?.length ?? 0,
    warning: data.warning?.length ?? 0,
    info: data.info?.length ?? 0,
  }

  // Pick the strip's color from the worst-severity bucket.
  const worst = counts.critical > 0 ? 'critical' : counts.warning > 0 ? 'warning' : 'info'
  const worstCfg = SEVERITY_CONFIG[worst]
  const HeaderIcon = worstCfg.icon

  return (
    <div className={clsx(
      'sticky top-0 z-20 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 py-3 border-b shadow-sm',
      worstCfg.bg, worstCfg.border
    )}>
      <button
        type="button"
        onClick={() => setAndPersist(!collapsed)}
        className="w-full flex items-center gap-3 text-left"
        aria-expanded={!collapsed}
      >
        <HeaderIcon className={clsx('h-5 w-5 flex-shrink-0', worstCfg.text)} />
        <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
          <span className={clsx('text-sm font-semibold', worstCfg.text)}>
            {total} {total === 1 ? 'thing needs' : 'things need'} your attention
          </span>
          <span className="flex items-center gap-2 text-xs text-gray-600">
            {counts.critical > 0 && (
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-red-500" />{counts.critical} critical
              </span>
            )}
            {counts.warning > 0 && (
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-amber-500" />{counts.warning} warning
              </span>
            )}
            {counts.info > 0 && (
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-blue-500" />{counts.info} info
              </span>
            )}
          </span>
        </div>
        <ChevronRight className={clsx(
          'h-4 w-4 flex-shrink-0 transition-transform',
          worstCfg.text,
          !collapsed && 'rotate-90'
        )} />
      </button>

      {!collapsed && (
        <div className="mt-3 space-y-3">
          {['critical', 'warning', 'info'].map((sev) => {
            const items = data[sev] || []
            if (items.length === 0) return null
            const cfg = SEVERITY_CONFIG[sev]
            const RowIcon = cfg.icon
            return (
              <div key={sev}>
                <div className={clsx('text-xs font-semibold uppercase tracking-wide mb-1.5', cfg.text)}>
                  {cfg.label} ({items.length})
                </div>
                <div className="space-y-1.5">
                  {items.map((a, i) => (
                    <div
                      key={`${sev}-${a.anomaly_type}-${i}`}
                      className={clsx(
                        'flex items-start gap-3 p-3 rounded-lg border bg-white',
                        cfg.border
                      )}
                    >
                      <RowIcon className={clsx('h-4 w-4 flex-shrink-0 mt-0.5', cfg.text)} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{a.title}</p>
                        {a.description && (
                          <p className="text-xs text-gray-600 mt-0.5">{a.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
          <p className="text-[11px] text-gray-400 text-right">
            Updated {data._generated_at ? new Date(data._generated_at).toLocaleTimeString() : 'just now'}
          </p>
        </div>
      )}
    </div>
  )
}

// =============================================================================
// LATEST REPORT CARD (Pillar 2)
// =============================================================================

function LatestReportCard({ ranchId }) {
  const { data: report } = useQuery({
    queryKey: ['latestReport', ranchId],
    queryFn: ({ signal }) => getLatestReport(ranchId, { signal }),
  })

  if (!report) return null

  const statusColors = {
    draft: 'bg-amber-100 text-amber-700',
    approved: 'bg-green-100 text-green-700',
    generating: 'bg-blue-100 text-blue-700',
  }

  return (
    <Link to={`/reports/${report.id}`} className="card p-4 hover:shadow-md transition-shadow block">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary-50 rounded-lg">
          <FileText className="h-5 w-5 text-primary-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900">Latest Report</p>
          <p className="text-xs text-gray-500 truncate">{report.title}</p>
        </div>
        <span className={clsx(
          'text-xs font-medium px-2 py-1 rounded-full capitalize',
          statusColors[report.status] || 'bg-gray-100 text-gray-600'
        )}>
          {report.status}
        </span>
        <ChevronRight className="h-4 w-4 text-gray-400" />
      </div>
    </Link>
  )
}

// =============================================================================
// NOTIFICATIONS PANEL
// =============================================================================

function NotificationsPanel({ ranchId, isOpen, onClose }) {
  const queryClient = useQueryClient()

  const { data: alerts, isLoading: alertsLoading } = useQuery({
    queryKey: ['agentAlerts', ranchId],
    queryFn: () => getAgentAlerts(ranchId),
    enabled: isOpen,
  })

  const dismissMutation = useMutation({
    mutationFn: (alertId) => dismissAlert(alertId),
    onSuccess: () => {
      queryClient.invalidateQueries(['agentAlerts', ranchId])
      queryClient.invalidateQueries(['agentDashboard', ranchId])
    }
  })

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Notifications</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto h-full pb-20">
          {alertsLoading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : alerts?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-gray-500">
              <Bell className="h-8 w-8 mb-2 opacity-50" />
              <p>No pending notifications</p>
            </div>
          ) : (
            <div className="divide-y">
              {alerts?.map((alert) => (
                <div key={alert.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start gap-3">
                    <div className={clsx(
                      'p-2 rounded-full',
                      alert.priority === 'critical' ? 'bg-red-100 text-red-600' :
                      alert.priority === 'high' ? 'bg-amber-100 text-amber-600' :
                      'bg-blue-100 text-blue-600'
                    )}>
                      {alert.priority === 'critical' ? <XCircle className="h-4 w-4" /> :
                       alert.priority === 'high' ? <AlertTriangle className="h-4 w-4" /> :
                       <Info className="h-4 w-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900">{alert.title}</p>
                      <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                      <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                        <Clock className="h-3 w-3" />
                        {new Date(alert.created_at).toLocaleString()}
                      </div>
                    </div>
                    <button
                      onClick={() => dismissMutation.mutate(alert.id)}
                      className="p-1 hover:bg-gray-200 rounded"
                      title="Dismiss"
                    >
                      <X className="h-4 w-4 text-gray-400" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// QUICK ACTIONS COMPONENT
// =============================================================================

function QuickActions() {
  const navigate = useNavigate()

  const actions = [
    { label: 'Log Event', icon: Plus, path: '/cattle/events/new', bg: 'bg-primary-50 hover:bg-primary-100', text: 'text-primary-700' },
    { label: 'Record Expense', icon: DollarSign, path: '/finance/expenses/new', bg: 'bg-red-50 hover:bg-red-100', text: 'text-red-700' },
    { label: 'New Biochar Batch', icon: Flame, path: '/biochar/batches/new', bg: 'bg-amber-50 hover:bg-amber-100', text: 'text-amber-700' },
    { label: 'View Map', icon: Map, path: '/paddocks/map', bg: 'bg-green-50 hover:bg-green-100', text: 'text-green-700' },
  ]

  return (
    <div className="card p-4">
      <h3 className="text-sm font-medium text-gray-700 mb-3">Quick Actions</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {actions.map((action) => (
          <button
            key={action.path}
            onClick={() => navigate(action.path)}
            className={clsx(
              'flex items-center gap-2 p-3 rounded-lg text-sm font-medium transition-colors',
              action.bg, action.text
            )}
          >
            <action.icon className="h-4 w-4" />
            {action.label}
          </button>
        ))}
      </div>
    </div>
  )
}

// =============================================================================
// HERDSMAN QUICK ACTIONS
// =============================================================================

function HerdsmanQuickActions() {
  const navigate = useNavigate()
  const actions = [
    { label: 'Log Event', icon: Plus, path: '/cattle/events/new' },
    { label: 'Record Weight', icon: TrendingUp, path: '/cattle/weigh-session' },
    { label: 'Upload Photos', icon: Camera, path: '/cattle/photo-upload' },
  ]
  return (
    <div className="card p-4">
      <h3 className="text-sm font-medium text-gray-700 mb-3">Quick Actions</h3>
      <div className="grid grid-cols-3 gap-2">
        {actions.map((action) => (
          <button
            key={action.path}
            onClick={() => navigate(action.path)}
            className="flex flex-col items-center gap-2 p-4 rounded-lg bg-gray-50 hover:bg-gray-100 text-sm font-medium text-gray-700"
          >
            <action.icon className="h-6 w-6" />
            {action.label}
          </button>
        ))}
      </div>
    </div>
  )
}

// =============================================================================
// DISCREPANCY FLAGS COMPONENT
// =============================================================================

function DiscrepancyFlags({ ranchId }) {
  const queryClient = useQueryClient()

  const { data: flags, isLoading } = useQuery({
    queryKey: ['discrepancies', ranchId],
    queryFn: () => getDiscrepancies(ranchId, 'open'),
  })

  const resolveMutation = useMutation({
    mutationFn: ({ flagId, notes }) => resolveDiscrepancy(flagId, notes, ranchId),
    onSuccess: () => {
      queryClient.invalidateQueries(['discrepancies', ranchId])
      queryClient.invalidateQueries(['agentDashboard', ranchId])
    }
  })

  const dismissMutation = useMutation({
    mutationFn: (flagId) => dismissDiscrepancy(flagId),
    onSuccess: () => {
      queryClient.invalidateQueries(['discrepancies', ranchId])
      queryClient.invalidateQueries(['agentDashboard', ranchId])
    }
  })

  if (isLoading) return null
  if (!flags || flags.length === 0) return null

  const severityConfig = {
    critical: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', icon: XCircle },
    warning: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', icon: AlertTriangle },
    info: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', icon: Info },
  }

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-700">
          Issues Requiring Attention ({flags.length})
        </h3>
        <Link to="/agents/discrepancies" className="text-sm text-primary-600 hover:underline">
          View all
        </Link>
      </div>
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {flags.slice(0, 5).map((flag) => {
          const config = severityConfig[flag.severity] || severityConfig.info
          const IconComponent = config.icon

          return (
            <div
              key={flag.id}
              className={clsx(
                'flex items-start gap-3 p-3 rounded-lg border',
                config.bg, config.border
              )}
            >
              <IconComponent className={clsx('h-5 w-5 flex-shrink-0 mt-0.5', config.text)} />
              <div className="flex-1 min-w-0">
                <p className={clsx('text-sm font-medium', config.text)}>
                  {flag.flag_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </p>
                <p className="text-sm text-gray-600 mt-0.5">{flag.description}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {new Date(flag.detected_at).toLocaleString()}
                </p>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => dismissMutation.mutate(flag.id)}
                  className="p-1 hover:bg-white/50 rounded"
                  title="Dismiss"
                >
                  <X className="h-4 w-4 text-gray-500" />
                </button>
                <button
                  onClick={() => resolveMutation.mutate({ flagId: flag.id, notes: 'Resolved from dashboard' })}
                  className="p-1 hover:bg-white/50 rounded"
                  title="Mark resolved"
                >
                  <CheckCircle className="h-4 w-4 text-green-600" />
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// =============================================================================
// GRAZING RECOMMENDATIONS COMPONENT
// =============================================================================

function GrazingRecommendations({ ranchId }) {
  const { data: recommendations, isLoading } = useQuery({
    queryKey: ['grazingRecommendations', ranchId],
    queryFn: () => getGrazingRecommendations(ranchId),
  })

  if (isLoading) return null
  if (!recommendations || recommendations.length === 0) return null

  const todayRec = recommendations[0]
  if (!todayRec || todayRec.accepted) return null

  return (
    <div className="card p-4 bg-green-50 border border-green-200">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-full bg-green-100">
          <Map className="h-5 w-5 text-green-600" />
        </div>
        <div className="flex-1">
          <p className="font-medium text-green-800">Grazing Recommendation</p>
          <p className="text-sm text-green-700 mt-1">
            Consider moving cattle to <strong>{todayRec.recommended_paddock}</strong>
          </p>
          <p className="text-xs text-green-600 mt-1">{todayRec.reason}</p>
        </div>
        <Link
          to={`/paddocks?action=move&target=${todayRec.recommended_paddock}`}
          className="btn-primary text-sm"
        >
          Move Now
        </Link>
      </div>
    </div>
  )
}

// =============================================================================
// RECENT ACTIVITY COMPONENT
// =============================================================================

function RecentActivity({ events }) {
  if (!events || events.length === 0) return null

  const eventIcons = {
    birth: { icon: Plus, color: 'text-green-600 bg-green-100' },
    death: { icon: XCircle, color: 'text-red-600 bg-red-100' },
    sale: { icon: DollarSign, color: 'text-blue-600 bg-blue-100' },
    treatment: { icon: Activity, color: 'text-purple-600 bg-purple-100' },
    weight: { icon: TrendingUp, color: 'text-amber-600 bg-amber-100' },
    move: { icon: ArrowRight, color: 'text-primary-600 bg-primary-100' },
  }

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-700">Recent Activity</h3>
        <Link to="/cattle/events" className="text-sm text-primary-600 hover:underline">
          View all
        </Link>
      </div>
      <div className="space-y-3">
        {events.slice(0, 5).map((event, index) => {
          const config = eventIcons[event.type] || eventIcons.treatment
          const IconComponent = config.icon

          return (
            <div key={index} className="flex items-center gap-3">
              <div className={clsx('p-2 rounded-full', config.color)}>
                <IconComponent className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900 truncate">{event.description}</p>
                <p className="text-xs text-gray-500">{event.time_ago}</p>
              </div>
              {event.verified && (
                <CheckCircle className="h-4 w-4 text-green-500" />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// =============================================================================
// AGENT STATUS INDICATOR
// =============================================================================

function AgentStatusIndicator({ ranchId }) {
  const { data: agentData } = useQuery({
    queryKey: ['agentDashboard', ranchId],
    queryFn: () => getAgentDashboard(ranchId),
  })

  if (!agentData) return null

  const { agent_runs, discrepancies, alerts } = agentData

  return (
    <div className="flex items-center gap-4 text-sm">
      <div className="flex items-center gap-1.5">
        <div className={clsx(
          'w-2 h-2 rounded-full',
          agent_runs.failed > 0 ? 'bg-red-500' : 'bg-green-500'
        )} />
        <span className="text-gray-600">
          {agent_runs.success}/{agent_runs.total_this_week} agents OK
        </span>
      </div>
      {discrepancies.open > 0 && (
        <div className="flex items-center gap-1.5 text-amber-600">
          <AlertTriangle className="h-4 w-4" />
          {discrepancies.open} issues
        </div>
      )}
      {alerts.pending > 0 && (
        <div className="flex items-center gap-1.5 text-blue-600">
          <Bell className="h-4 w-4" />
          {alerts.pending} alerts
        </div>
      )}
    </div>
  )
}

// =============================================================================
// CATEGORY BREAKDOWN COMPONENT
// =============================================================================

function CategoryBreakdown({ title, data, colorMap }) {
  const total = Object.values(data).reduce((sum, val) => sum + val, 0)

  return (
    <div>
      <h4 className="text-sm font-medium text-gray-700 mb-3">{title}</h4>
      <div className="space-y-2">
        {Object.entries(data).map(([key, value]) => {
          const percentage = total > 0 ? (value / total) * 100 : 0
          return (
            <div key={key} className="flex items-center gap-3">
              <div className="w-20 text-sm text-gray-600 capitalize">{key}</div>
              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={clsx('h-full rounded-full', colorMap?.[key] || 'bg-primary-500')}
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <div className="w-10 text-sm text-gray-900 text-right">{value}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// =============================================================================
// VEGETATION HEALTH CARD
// =============================================================================

function VegetationHealthCard({ snapshot }) {
  const withNdvi = snapshot.filter(s => s.paddock_ndvi != null)
  const avgNdvi = withNdvi.length > 0
    ? withNdvi.reduce((sum, s) => sum + Number(s.paddock_ndvi), 0) / withNdvi.length
    : null

  const healthCounts = { stressed: 0, moderate: 0, healthy: 0, vigorous: 0 }
  snapshot.forEach(s => {
    if (s.vegetation_health && healthCounts[s.vegetation_health] !== undefined) {
      healthCounts[s.vegetation_health]++
    }
  })

  const dates = snapshot.filter(s => s.observation_date).map(s => new Date(s.observation_date))
  const latestDate = dates.length > 0 ? new Date(Math.max(...dates)) : null

  const healthBarColors = {
    stressed: 'bg-red-500',
    moderate: 'bg-yellow-500',
    healthy: 'bg-green-500',
    vigorous: 'bg-emerald-500',
  }

  const total = Object.values(healthCounts).reduce((a, b) => a + b, 0)

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
          <Satellite className="h-5 w-5 text-indigo-600" />
          Vegetation Health
        </h3>
        <Link to="/paddocks" className="text-sm text-primary-600 hover:underline flex items-center gap-1">
          Paddocks <ChevronRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        {avgNdvi != null && (
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-700">{avgNdvi.toFixed(3)}</div>
            <div className="text-sm text-green-600">Pasture Health</div>
          </div>
        )}
        <div className="text-center p-4 bg-indigo-50 rounded-lg">
          <div className="text-2xl font-bold text-indigo-700">{snapshot.length}</div>
          <div className="text-sm text-indigo-600">Paddocks Monitored</div>
        </div>
      </div>

      {total > 0 && (
        <div>
          <p className="text-sm text-gray-600 mb-2">Health Distribution</p>
          <div className="flex rounded-full h-3 overflow-hidden mb-2">
            {Object.entries(healthCounts).filter(([, v]) => v > 0).map(([health, count]) => (
              <div
                key={health}
                className={`${healthBarColors[health]} transition-all`}
                style={{ width: `${(count / total) * 100}%` }}
                title={`${health}: ${count}`}
              />
            ))}
          </div>
          <div className="flex flex-wrap gap-3">
            {Object.entries(healthCounts).filter(([, v]) => v > 0).map(([health, count]) => (
              <div key={health} className="flex items-center gap-1 text-xs text-gray-600">
                <div className={`w-2.5 h-2.5 rounded-full ${healthBarColors[health]}`} />
                <span className="capitalize">{health}</span> {count}
              </div>
            ))}
          </div>
        </div>
      )}

      {latestDate && (
        <p className="text-xs text-gray-400 mt-3">
          Last data: {latestDate.toLocaleDateString()}
        </p>
      )}
    </div>
  )
}

// =============================================================================
// FINANCE SUMMARY SECTION
// =============================================================================

function FinanceSummary({ finance, pendingDataNote }) {
  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-medium text-gray-900">Financial Summary (30 days)</h3>
          {pendingDataNote && (
            <Info
              className="h-4 w-4 text-gray-400"
              title={pendingDataNote}
              aria-label={pendingDataNote}
            />
          )}
        </div>
        <Link to="/finance" className="text-sm text-primary-600 hover:underline flex items-center gap-1">
          Details <ChevronRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="space-y-4">
        <div className="flex justify-between items-center py-2 border-b">
          <span className="text-gray-600">Total Income</span>
          <span className="text-lg font-medium text-green-600">{formatCurrency(finance.total_income)}</span>
        </div>
        <div className="flex justify-between items-center py-2 border-b">
          <span className="text-gray-600">Total Expenses</span>
          <span className="text-lg font-medium text-red-600">{formatCurrency(finance.total_expenses)}</span>
        </div>
        <div className="flex justify-between items-center py-2">
          <span className="text-gray-900 font-medium">Net Profit</span>
          <span className={clsx(
            'text-xl font-bold',
            finance.net_profit >= 0 ? 'text-green-600' : 'text-red-600'
          )}>
            {formatCurrency(finance.net_profit)}
          </span>
        </div>
      </div>

      {finance.top_expense_categories?.length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Top Expenses</h4>
          <div className="space-y-2">
            {finance.top_expense_categories.slice(0, 3).map((cat) => (
              <div key={cat.name} className="flex justify-between text-sm">
                <span className="text-gray-600">{cat.name}</span>
                <span className="text-gray-900">{formatCurrency(cat.total)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// =============================================================================
// BIOCHAR SUMMARY SECTION
// =============================================================================

function BiocharSummary({ biochar }) {
  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Biochar Operations</h3>
        <Link to="/biochar" className="text-sm text-primary-600 hover:underline flex items-center gap-1">
          Details <ChevronRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="text-center p-4 bg-amber-50 rounded-lg">
          <div className="text-2xl font-bold text-amber-700">{biochar.total_batches}</div>
          <div className="text-sm text-amber-600">Total Batches</div>
        </div>
        <div className="text-center p-4 bg-green-50 rounded-lg">
          <div className="text-2xl font-bold text-green-700">{biochar.total_produced_kg.toFixed(0)}</div>
          <div className="text-sm text-green-600">kg Produced</div>
        </div>
        <div className="text-center p-4 bg-blue-50 rounded-lg">
          <div className="text-2xl font-bold text-blue-700">{biochar.patches_identified}</div>
          <div className="text-sm text-blue-600">Patches Found</div>
        </div>
        <div className="text-center p-4 bg-primary-50 rounded-lg">
          <div className="text-2xl font-bold text-primary-700">{biochar.patches_cleared}</div>
          <div className="text-sm text-primary-600">Patches Cleared</div>
        </div>
      </div>
      {biochar.pending_audits > 0 && (
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm text-amber-800">
              {biochar.pending_audits} batches pending audit
            </span>
            <Link to="/biochar/audits" className="text-sm text-amber-700 font-medium hover:underline">
              Review
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}

// =============================================================================
// HERDSMAN DASHBOARD (Pillar 3 — simplified view)
// =============================================================================

function HerdsmanDashboard({ ranchId, data }) {
  const { herd, recent_events } = data

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Tasks</h1>
        <p className="text-gray-500 mt-1">Record events and manage herd</p>
      </div>

      <HerdsmanQuickActions />

      <StatCard
        title="Active Cattle"
        value={herd.active_count}
        subtitle={`${herd.total_count} total`}
        icon={Beef}
        color="primary"
        link="/cattle"
      />

      <GrazingRecommendations ranchId={ranchId} />

      <RecentActivity events={recent_events} />
    </div>
  )
}

// =============================================================================
// OWNER DASHBOARD (Pillar 3 — financial focus)
// =============================================================================

function OwnerDashboard({ ranchId, data, metrics }) {
  const { herd, finance, notes } = data
  const pendingDataNote = notes?.pending_data
  // Session 4.1 — backend gates finance behind role_at_ranch='owner'. Viewers
  // route here too but are NOT owners, so finance is null for them; drop the
  // money tiles and the FinanceSummary card in that case.
  const showFinance = finance != null

  return (
    <div className="space-y-6">
      <ActionRequiredStrip ranchId={ranchId} />

      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 mt-1">{data.ranch_name} — Owner View</p>
      </div>

      <div className={clsx(
        'grid grid-cols-1 gap-4',
        showFinance ? 'sm:grid-cols-2 md:grid-cols-3' : 'sm:grid-cols-1',
      )}>
        {showFinance && (
          <StatCard
            title="Net Profit (30d)"
            value={formatCurrency(finance.net_profit)}
            subtitle={finance.net_profit >= 0 ? 'Profitable' : 'Loss'}
            icon={DollarSign}
            color={finance.net_profit >= 0 ? 'green' : 'red'}
            trend={metrics?.net_profit_30d?.trend_pct}
            link="/finance"
          />
        )}
        <StatCard
          title="Active Cattle"
          value={herd.active_count}
          subtitle={`${herd.total_count} total`}
          icon={Beef}
          color="primary"
          trend={metrics?.headcount?.trend_pct}
          link="/cattle"
        />
        {showFinance && (
          <StatCard
            title="Total Income (30d)"
            value={formatCurrency(finance.total_income)}
            icon={TrendingUp}
            color="green"
            trend={metrics?.total_income_30d?.trend_pct}
          />
        )}
      </div>

      {/* Plan B2 — the last dip day at the kraal: who was seen, with faces.
          Scoped to the owner's own animals when the account is linked. */}
      <KraalFeedCard ranchId={ranchId} />

      {/* Phase 4a — the owner's animals at the crush. Renders only when this
          account is linked to an owner segment (User management). */}
      <RegistrationCard ranchId={ranchId} />

      <LatestReportCard ranchId={ranchId} />

      {/* Grazing-intelligence Phase 8a — where the herd has grazed (recency,
          recovery, satellite-verified rotation). Sits above the vegetation grid. */}
      <GrazingTrackerCard ranchId={ranchId} />

      {/* Owner-view plan, Session 3 — paddock vegetation + grazing overlay */}
      <PaddockHealthCard ranchId={ranchId} />
      <GrazingNowCard ranchId={ranchId} />

      {/* Owner-view plan, Session 2 — activity depth */}
      <ActivityPulseCard ranchId={ranchId} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <WeighingTrendCard ranchId={ranchId} />
        <HealthInterventionsCard ranchId={ranchId} />
      </div>

      {showFinance && <FinanceSummary finance={finance} pendingDataNote={pendingDataNote} />}
    </div>
  )
}

// =============================================================================
// MANAGER / FULL DASHBOARD (Pillar 3 — operational view + Pillar 2 features)
// =============================================================================

function ManagerDashboard({ ranchId, data, metrics }) {
  const { canWrite } = useAuth()
  const [notificationsOpen, setNotificationsOpen] = useState(false)

  const { data: agentData } = useQuery({
    queryKey: ['agentDashboard', ranchId],
    queryFn: () => getAgentDashboard(ranchId),
  })

  const { data: vegSnapshot } = useQuery({
    queryKey: ['remoteSensingSnapshot', ranchId],
    queryFn: () => getRemoteSensingSnapshot(ranchId),
  })

  const { herd, paddocks, finance, biochar, alerts, recent_events, notes } = data
  const pendingAlerts = agentData?.alerts?.pending || 0
  // Session 4.1 — managers never get finance from the backend. Gate on null
  // rather than role so a system_admin (who does get finance) still sees it.
  const showFinance = finance != null
  const pendingDataNote = notes?.pending_data

  return (
    <div className="space-y-6">
      <ActionRequiredStrip ranchId={ranchId} />
      {/* Pin-protocol compliance — shadow mode; renders only once a pin
          program is measured (pasture-pin plan Phase 5). */}
      <PinComplianceCard ranchId={ranchId} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 mt-1">{data.ranch_name} Overview</p>
        </div>
        <div className="flex items-center gap-4">
          <AgentStatusIndicator ranchId={ranchId} />
          <button
            onClick={() => setNotificationsOpen(true)}
            className="relative p-2 hover:bg-gray-100 rounded-lg"
          >
            <Bell className="h-5 w-5 text-gray-600" />
            {pendingAlerts > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                {pendingAlerts > 9 ? '9+' : pendingAlerts}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      {canWrite && <QuickActions />}

      {/* Grazing Recommendation */}
      <GrazingRecommendations ranchId={ranchId} />

      {/* Discrepancy Flags */}
      <DiscrepancyFlags ranchId={ranchId} />

      {/* Legacy Alerts */}
      {alerts && alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((alert, index) => (
            <div
              key={index}
              className={clsx(
                'flex items-start gap-3 p-4 rounded-lg border',
                alert.type === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-800' :
                alert.type === 'error' ? 'bg-red-50 border-red-200 text-red-800' :
                'bg-blue-50 border-blue-200 text-blue-800'
              )}
            >
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm">{alert.message}</p>
            </div>
          ))}
        </div>
      )}

      {/* Stats Grid (enhanced with trends from Pillar 2) */}
      <div className={clsx(
        'grid grid-cols-1 gap-4',
        showFinance ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-3',
      )}>
        <StatCard
          title="Active Cattle"
          value={herd.active_count}
          subtitle={`${herd.total_count} total`}
          icon={Beef}
          color="primary"
          trend={metrics?.headcount?.trend_pct}
          link="/cattle"
        />
        <StatCard
          title="Paddocks Grazing"
          value={paddocks.grazing}
          subtitle={`${paddocks.ready} ready to graze`}
          icon={Map}
          color="green"
          link="/paddocks"
        />
        {showFinance && (
          <StatCard
            title="Net Profit (30d)"
            value={formatCurrency(finance.net_profit)}
            subtitle={finance.net_profit >= 0 ? 'Profitable' : 'Loss'}
            icon={DollarSign}
            color={finance.net_profit >= 0 ? 'green' : 'red'}
            trend={metrics?.net_profit_30d?.trend_pct}
            link="/finance"
          />
        )}
        <StatCard
          title="Biochar Produced"
          value={`${biochar.total_produced_kg.toFixed(0)} kg`}
          subtitle={`${biochar.patches_cleared} patches cleared`}
          icon={Flame}
          color="amber"
          trend={metrics?.biochar_produced_kg?.trend_pct}
          link="/biochar"
        />
      </div>

      {/* Plan B2 — the last dip day at the kraal (the whole herd for a manager:
          how much of it the kraal identified is the staffing signal). */}
      <KraalFeedCard ranchId={ranchId} />

      {/* Latest Report (Pillar 2) */}
      <LatestReportCard ranchId={ranchId} />

      {/* Detailed Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Herd Breakdown */}
        <div className="section-card">
          <div className="section-card-header">
            <h3 className="section-card-title">Herd Composition</h3>
            <Link to="/cattle" className="text-sm text-primary-600 hover:underline flex items-center gap-1">
              View all <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="section-card-body">
            <CategoryBreakdown
              title="By Category"
              data={herd.by_category}
              colorMap={{
                cows: 'bg-primary-500',
                bulls: 'bg-blue-500',
                heifers: 'bg-green-500',
                calves: 'bg-amber-500',
                steers: 'bg-purple-500',
                weaners: 'bg-pink-500',
              }}
            />
          </div>
        </div>

        {/* Paddock Status */}
        <div className="section-card">
          <div className="section-card-header">
            <h3 className="section-card-title">Paddock Status</h3>
            <Link to="/paddocks" className="text-sm text-primary-600 hover:underline flex items-center gap-1">
              View map <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="section-card-body">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-4 bg-green-50 rounded-xl ring-1 ring-green-100">
                <div className="text-2xl font-bold text-green-700">{paddocks.grazing}</div>
                <div className="text-sm text-green-600">Grazing</div>
              </div>
              <div className="p-4 bg-amber-50 rounded-xl ring-1 ring-amber-100">
                <div className="text-2xl font-bold text-amber-700">{paddocks.resting}</div>
                <div className="text-sm text-amber-600">Resting</div>
              </div>
              <div className="p-4 bg-blue-50 rounded-xl ring-1 ring-blue-100">
                <div className="text-2xl font-bold text-blue-700">{paddocks.ready}</div>
                <div className="text-sm text-blue-600">Ready</div>
              </div>
            </div>
            <div className="mt-4 text-center text-sm text-gray-500">
              {paddocks.total_area_ha?.toFixed(0) || 0} ha total area
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <RecentActivity events={recent_events} />
      </div>

      {/* Owner-view plan, Session 3 — paddock vegetation + grazing overlay (managers too) */}
      <PaddockHealthCard ranchId={ranchId} />
      <GrazingNowCard ranchId={ranchId} />

      {/* Owner-view plan, Session 2 — activity depth (managers too) */}
      <ActivityPulseCard ranchId={ranchId} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <WeighingTrendCard ranchId={ranchId} />
        <HealthInterventionsCard ranchId={ranchId} />
      </div>

      {/* Second Row — finance tile hidden for non-owner roles */}
      <div className={clsx(
        'grid grid-cols-1 gap-6',
        showFinance && 'lg:grid-cols-2',
      )}>
        {showFinance && <FinanceSummary finance={finance} pendingDataNote={pendingDataNote} />}
        <BiocharSummary biochar={biochar} />
      </div>

      {/* Vegetation Health */}
      {vegSnapshot && vegSnapshot.length > 0 && (
        <VegetationHealthCard snapshot={vegSnapshot} />
      )}

      {/* Notifications Panel */}
      <NotificationsPanel
        ranchId={ranchId}
        isOpen={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
      />
    </div>
  )
}

// =============================================================================
// MAIN DASHBOARD — ROLE ROUTER
// =============================================================================

// Narrow roles don't have a dashboard — redirect straight to their landing page.
// Spec rules 10/11/12: bush_clearer → My Lantana Assignments,
// biochar_operator → My Biochar Batches, land_monitor → Land Monitoring.
const NARROW_ROLE_LANDING = {
  bush_clearer: '/my-lantana',
  biochar_operator: '/my-biochar',
  land_monitor: '/land',
}

export default function Dashboard({ ranchId }) {
  const auth = useAuth()
  const role = auth?.role || 'owner'

  // Narrow roles don't have access to /api/v1/dashboard. We still declare
  // the hooks unconditionally (rules-of-hooks) but gate the query with
  // `enabled` so no 403 fires, then short-circuit the render below.
  const isNarrow = !!NARROW_ROLE_LANDING[role]

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['dashboard', ranchId],
    queryFn: ({ signal }) => getDashboard(ranchId, { signal }),
    enabled: !isNarrow,
    refetchInterval: 60000,
  })

  const { data: metrics } = useQuery({
    queryKey: ['dashboardMetrics', ranchId],
    queryFn: ({ signal }) => getDashboardMetrics(ranchId, { signal }),
    enabled: !isNarrow,
    refetchInterval: 60000,
  })

  if (isNarrow) {
    return <Navigate to={NARROW_ROLE_LANDING[role]} replace />
  }

  // Note: auto-refresh handled by refetchInterval on useQuery above

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error) {
    return <DataLoadError error={error} title="Failed to load dashboard" onRetry={() => refetch()} />
  }

  // Role-based dashboard selection (Pillar 3)
  if (role === 'herdsman') {
    return <HerdsmanDashboard ranchId={ranchId} data={data} />
  }

  // Viewer sees the same tabs as owner (spec rule 9) — route to OwnerDashboard.
  if (role === 'owner' || role === 'viewer') {
    return <OwnerDashboard ranchId={ranchId} data={data} metrics={metrics} />
  }

  // Manager, system_admin — full operational view
  return <ManagerDashboard ranchId={ranchId} data={data} metrics={metrics} />
}
